// Answers and events of the runs routes (platform p1f at f3a2d0f: runs/views.py, runs/rag.py,
// runs/relay.py, cosmosai_api/runs.py), camelCase on the wire (ADR 0015).
import { z } from "zod";

export const RunStatus = z.enum(["queued", "running", "done", "failed", "cancelled"]);
export type RunStatus = z.infer<typeof RunStatus>;

/** The statuses a run never leaves. */
export const FINAL: ReadonlySet<RunStatus> = new Set(["done", "failed", "cancelled"]);

/** An ISO 4217 code, so `Intl.NumberFormat` never throws on it. */
const Currency = z.string().regex(/^[A-Z]{3}$/);

export const Cost = z.object({ amount: z.string(), currency: Currency });
export type Cost = z.infer<typeof Cost>;

/** One model call of a run; fields the Studio does not read are kept. */
export const CallUsage = z.looseObject({
  kind: z.string(),
  alias: z.string(),
  status: z.string(),
  inputTokens: z.number().int().nullable(),
  outputTokens: z.number().int().nullable(),
  cost: z.string().nullable(),
  currency: Currency.nullable(),
  latencyMs: z.number(),
});
export type CallUsage = z.infer<typeof CallUsage>;

export const RunWarning = z.object({ code: z.string(), message: z.string() });
export type RunWarning = z.infer<typeof RunWarning>;

export const RunView = z.object({
  id: z.string(),
  status: RunStatus,
  agentType: z.string(),
  /** `name@version` of the agent that ran. */
  agent: z.string(),
  environment: z.string(),
  conversation: z.string().nullable(),
  input: z.record(z.string(), z.unknown()),
  output: z.record(z.string(), z.unknown()).nullable(),
  details: z.record(z.string(), z.unknown()),
  warnings: z.array(RunWarning),
  error: z.object({ code: z.string(), message: z.string() }).nullable(),
  usage: z.array(CallUsage),
  cost: Cost.nullable(),
  timings: z.record(z.string(), z.number()),
  createdAt: z.string(),
  startedAt: z.string().nullable(),
  finishedAt: z.string().nullable(),
  cancelRequested: z.boolean(),
});
export type RunView = z.infer<typeof RunView>;

export const ConversationView = z.object({
  id: z.string(),
  agent: z.string(),
  environment: z.string(),
  title: z.string(),
  createdAt: z.string(),
  updatedAt: z.string(),
  /** Only when the conversation is read alone; oldest first. */
  runs: z.array(RunView).optional(),
});
export type ConversationView = z.infer<typeof ConversationView>;

export const CreatedConversation = ConversationView.extend({ conversationId: z.string() });
export type CreatedConversation = z.infer<typeof CreatedConversation>;

export const StartedRun = z.object({
  runId: z.string(),
  status: RunStatus,
  /** The run's events path, `/api/v1/…` included; same origin only, since it gets the token. */
  events: z.string().regex(/^\/api\/v1\//),
});
export type StartedRun = z.infer<typeof StartedRun>;

// The rag type's shapes (runs/rag.py): its input, output, details and passages.
export const RagInput = z.object({ question: z.string() });
export const RagOutput = z.object({ text: z.string() });

export const Passage = z.object({
  number: z.number().int(),
  title: z.string().nullable(),
  page: z.number().int().nullable(),
  pageLabel: z.string().nullable(),
  documentId: z.string(),
  chunk: z.string(),
  score: z.number().nullable(),
  /** The chunk's text while it exists; empty or absent once its document is erased. */
  text: z.string().optional(),
});
export type Passage = z.infer<typeof Passage>;

export const Cited = Passage.pick({
  number: true,
  documentId: true,
  title: true,
  page: true,
  chunk: true,
});
export type Cited = z.infer<typeof Cited>;

export const RagDetails = z.looseObject({
  rewritten: z.string().nullable(),
  passages: z.array(Passage),
  citations: z.array(z.number().int()),
  generationId: z.string(),
  cached: z.boolean(),
});
export type RagDetails = z.infer<typeof RagDetails>;

/** The payload of each event type a run publishes (the platform's ADR 0027). */
export const runEventPayloads = {
  stage: z.object({ name: z.string() }),
  passages: z.object({ items: z.array(Passage) }),
  token: z.object({ text: z.string() }),
  citations: z.object({ items: z.array(Cited) }),
  warning: RunWarning,
  done: z.looseObject({
    runId: z.string(),
    cached: z.boolean().optional(),
    usage: z.array(CallUsage),
    cost: Cost.nullable(),
    timings: z.record(z.string(), z.number()),
  }),
  failed: z.object({ runId: z.string(), code: z.string().nullable(), message: z.string() }),
  cancelled: z.object({ runId: z.string() }),
} as const;
export type RunEventType = keyof typeof runEventPayloads;
export type RunEventPayload<T extends RunEventType> = z.infer<(typeof runEventPayloads)[T]>;

/** The event types that end a run's stream. */
export const TERMINAL_EVENTS: ReadonlySet<RunEventType> = new Set(["done", "failed", "cancelled"]);

/** Total input and output tokens of a run's calls (null counts as 0). */
export function tokensOf(usage: readonly CallUsage[]): { input: number; output: number } {
  return usage.reduce(
    (sum, call) => ({
      input: sum.input + (call.inputTokens ?? 0),
      output: sum.output + (call.outputTokens ?? 0),
    }),
    { input: 0, output: 0 },
  );
}
